// 定数 (a, F: 世界測地系-測地基準系1980（GRS80）楕円体)
const m0 = 0.9999;
const a = 6378137;
const F = 298.257222101;

/**
 * 緯度・経度（10進数）を緯度・経度（60進数）に変換
 *
 * @param base10
 * @returns {string}
 */
export const base10ToBase60 = base10 => {
  const d = base10 > 0 ? Math.floor(base10) : Math.ceil(base10);
  var ms = Number("0." + String(base10).split(".")[1]) * 60;
  if (isNaN(ms)) ms = 0;
  var m = Math.floor(ms);
  var s = Number("0." + String(ms).split(".")[1]) * 60;
  if (isNaN(s) || s === 0) {
    s = "00.000";
  } else {
    const base = 100000;
    s = Math.round(s * base) / base;
    if (s < 10) s = "0" + String(s);
  }
  return String(d) + String(m) + String(s);
};

/**
 * 緯度・経度（60進数）を緯度・経度（10進数）に変換
 *
 * @param base60
 * @returns {number}
 */
export const base60ToBase10 = base60 => {
  const isLatitude = String(base60).split(".")[0].length === 6;
  const d = Number(String(base60).slice(0, isLatitude ? 2 : 3));
  const m = Number(
    String(base60).slice(isLatitude ? 2 : 3, isLatitude ? 4 : 5) / 60
  );
  const s = Number(
    String(base60).slice(isLatitude ? 4 : 5, base60.length) / 3600
  );
  return d + m + s;
};

/**
 * 緯度・経度（10進数）を平面直角座標系に変換
 *
 * @param {number} lat - 緯度
 * @param {number} lon - 経度
 * @param {Array<number>} rectangularCoordinate - 平面直角座標系原点
 */
export const base10ToXY = (lat, lon, rectangularCoordinate) => {
  const nj = require("numjs");

  // 緯度経度・平面直角座標系原点をラジアンに直す
  const phiRad = deg10ToRad(lat);
  const lambdaRad = deg10ToRad(lon);
  const phi0Rad = deg60ToRad(rectangularCoordinate[0]);
  const lambda0Rad = deg60ToRad(rectangularCoordinate[1]);

  // (1) n, A_i, alpha_iの計算
  const n = 1 / (2 * F - 1);
  const aArray = getAArray(n);
  const alphaArray = getAlphaArray(n);

  // (2), S, Aの計算
  const A_ = getA(n, aArray); // [m]
  const S_ = getS(n, aArray, phi0Rad);

  // (3) lambda_c, lambda_sの計算
  const lambdaC = Math.cos(lambdaRad - lambda0Rad);
  const lambdaS = Math.sin(lambdaRad - lambda0Rad);

  // (4) t, t_の計算
  const t = Math.sinh(
    Math.atanh(Math.sin(phiRad)) -
      ((2 * Math.sqrt(n)) / (1 + n)) *
        Math.atanh(((2 * Math.sqrt(n)) / (1 + n)) * Math.sin(phiRad))
  );
  const t_ = Math.sqrt(1 + t * t);

  // (5) xi', eta'の計算
  const xi2 = Math.atan(t / lambdaC); // [rad]
  const eta2 = Math.atanh(lambdaS / t_);

  // (6) x, yの計算
  const aa = alphaArray.slice(1);
  const x1 = arange(5).map(i => Math.sin(i * (2 * xi2)));
  const y1 = arange(5).map(i => Math.cos(i * (2 * xi2)));
  const x2 = arange(5).map(i => Math.cosh(i * (2 * eta2)));
  const y2 = arange(5).map(i => Math.sinh(i * (2 * eta2)));
  const x = A_ * (xi2 + nj.sum(nj.multiply(aa, nj.multiply(x1, x2)))) - S_; // [m]
  const y = A_ * (eta2 + nj.sum(nj.multiply(aa, nj.multiply(y1, y2)))); // [m]
  return String(x) + "," + String(y);
};

/**
 * 補助関数
 */
export const getAArray = n => {
  const A0 = 1 + n ** 2 / 4 + n ** 4 / 64;
  const A1 = -(3 / 2) * (n - n ** 3 / 8 - n ** 5 / 64);
  const A2 = (15 / 16) * (n ** 2 - n ** 4 / 4);
  const A3 = -(35 / 48) * (n ** 3 - (5 / 16) * n ** 5);
  const A4 = (315 / 512) * n ** 4;
  const A5 = -(693 / 1280) * n ** 5;
  return [A0, A1, A2, A3, A4, A5];
};

/**
 * 補助関数
 */
export const getAlphaArray = n => {
  const a0 = 0; // dummy
  const a1 =
    (1 / 2) * n -
    (2 / 3) * n ** 2 +
    (5 / 16) * n ** 3 +
    (41 / 180) * n ** 4 -
    (127 / 288) * n ** 5;
  const a2 =
    (13 / 48) * n ** 2 -
    (3 / 5) * n ** 3 +
    (557 / 1440) * n ** 4 +
    (281 / 630) * n ** 5;
  const a3 =
    (61 / 240) * n ** 3 - (103 / 140) * n ** 4 + (15061 / 26880) * n ** 5;
  const a4 = (49561 / 161280) * n ** 4 - (179 / 168) * n ** 5;
  const a5 = (34729 / 80640) * n ** 5;
  return [a0, a1, a2, a3, a4, a5];
};

/**
 * 平面直角座標系を緯度・経度（10進数）に変換
 *
 * @param {number} x - x座標
 * @param {number} y - y座標
 * @param {Array<number>} rectangularCoordinate - 平面直角座標系原点
 */
export const xyToBase10 = (x, y, rectangularCoordinate) => {
  const nj = require("numjs");
  // 平面直角座標系原点をラジアンに直す
  const phi0Rad = deg60ToRad(rectangularCoordinate[0]);
  const lambda0Rad = deg60ToRad(rectangularCoordinate[1]);

  // (1) n, B_i, beta_i, delta_iの計算
  const n = 1 / (2 * F - 1);
  const bArray = getBArray(n);
  const betaArray = getBetaArray(n);
  const deltaArray = getDeltaArray(n);

  // (2), S, Aの計算
  const A_ = getA(n, bArray);
  const S_ = getS(n, bArray, phi0Rad);

  // (3) xi, etaの計算
  const xi = (x + S_) / A_;
  const eta = y / A_;

  // (4) xi', eta'の計算
  const xiSin = arange(5).map(i => Math.sin(i * (2 * xi)));
  const xiCos = arange(5).map(i => Math.cos(i * (2 * xi)));
  const etaCosh = arange(5).map(i => Math.cosh(i * (2 * eta)));
  const etaSinh = arange(5).map(i => Math.sinh(i * (2 * eta)));
  const xi2 =
    xi - nj.sum(nj.multiply(betaArray.slice(1), nj.multiply(xiSin, etaCosh)));
  const eta2 =
    eta - nj.sum(nj.multiply(betaArray.slice(1), nj.multiply(xiCos, etaSinh)));

  // (5) chiの計算
  const chi = Math.asin(Math.sin(xi2) / Math.cosh(eta2)); // [rad]
  const chiSin = arange(6).map(i => Math.sin(i * (2 * chi)));

  // (6) 緯度(latitude), 経度(longitude)の計算
  const latitude = chi + nj.dot(deltaArray.slice(1), chiSin).selection.data[0]; // [rad]
  const longitude = lambda0Rad + Math.atan(Math.sinh(eta2) / Math.cos(xi2)); // [rad]

  return [toDegree(longitude), toDegree(latitude)];
};

/**
 * 補助関数
 */
export const getBArray = n => {
  const B0 = 1 + n ** 2 / 4 + n ** 4 / 64;
  const B1 = -(3 / 2) * (n - n ** 3 / 8 - n ** 5 / 64);
  const B2 = (15 / 16) * (n ** 2 - n ** 4 / 4);
  const B3 = -(35 / 48) * (n ** 3 - (5 / 16) * n ** 5);
  const B4 = (315 / 512) * n ** 4;
  const B5 = -(693 / 1280) * n ** 5;
  return [B0, B1, B2, B3, B4, B5];
};

/**
 * 補助関数
 */
export const getBetaArray = n => {
  const b0 = 0; // dummy
  const b1 =
    (1 / 2) * n -
    (2 / 3) * n ** 2 +
    (37 / 96) * n ** 3 -
    (1 / 360) * n ** 4 -
    (81 / 512) * n ** 5;
  const b2 =
    (1 / 48) * n ** 2 +
    (1 / 15) * n ** 3 -
    (437 / 1440) * n ** 4 +
    (46 / 105) * n ** 5;
  const b3 = (17 / 480) * n ** 3 - (37 / 840) * n ** 4 - (209 / 4480) * n ** 5;
  const b4 = (4397 / 161280) * n ** 4 - (11 / 504) * n ** 5;
  const b5 = (4583 / 161280) * n ** 5;
  return [b0, b1, b2, b3, b4, b5];
};

/**
 * 補助関数
 */
export const getDeltaArray = n => {
  const d0 = 0; // dummy
  const d1 =
    2 * n -
    (2 / 3) * n ** 2 -
    2 * n ** 3 +
    (116 / 45) * n ** 4 +
    (26 / 45) * n ** 5 -
    (2854 / 675) * n ** 6;
  const d2 =
    (7 / 3) * n ** 2 -
    (8 / 5) * n ** 3 -
    (227 / 45) * n ** 4 +
    (2704 / 315) * n ** 5 +
    (2323 / 945) * n ** 6;
  const d3 =
    (56 / 15) * n ** 3 -
    (136 / 35) * n ** 4 -
    (1262 / 105) * n ** 5 +
    (73814 / 2835) * n ** 6;
  const d4 =
    (4279 / 630) * n ** 4 - (332 / 35) * n ** 5 - (399572 / 14175) * n ** 6;
  const d5 = (4174 / 315) * n ** 5 - (144838 / 6237) * n ** 6;
  const d6 = (601676 / 22275) * n ** 6;
  return [d0, d1, d2, d3, d4, d5, d6];
};

/**
 * 補助関数
 */
export const arange = range => {
  return [...Array(range).keys()].map(i => ++i);
};

/**
 * 補助関数
 */
export const getA = (n, array) => {
  return ((m0 * a) / (1 + n)) * array[0]; // [m]
};

/**
 * 補助関数
 */
export const getS = (n, array, phi0Rad) => {
  const nj = require("numjs");
  return (
    ((m0 * a) / (1 + n)) *
    (array[0] * phi0Rad +
      nj.dot(
        array.slice(1),
        nj.sin(arange(5).map(i => i * (2 * phi0Rad))).selection.data
      ).selection.data[0])
  );
};

/**
 * 緯度経度(10進数)から radian への変換
 *
 * @param degrees
 * @returns {number}
 */
const deg10ToRad = degrees => {
  return (degrees * Math.PI) / 180;
};

/**
 * 緯度経度(度分秒)から radian への変換
 *
 * @param degrees
 * @returns {number}
 */
const deg60ToRad = degrees => {
  let radianBase = Math.PI / 180.0;
  let changeSign = 1; //符号変換
  if (degrees < 0) {
    changeSign = -1.0;
  }

  degrees = Math.abs(degrees);

  let angle = Math.floor(degrees / 10000.0);
  let minute = Math.floor(degrees / 100.0 - angle * 100.0);
  let second = degrees - angle * 10000.0 - minute * 100.0;
  let radian = changeSign * (angle + (minute + second / 60.0) / 60.0);

  return radian * radianBase;
};

/**
 * ラジアンを度に変換
 *
 * @param radian
 * @returns {number}
 */
const toDegree = radian => {
  if (isNaN(radian)) return NaN;
  return (radian * 360) / (2 * Math.PI);
};
